import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";
import {ClientsService} from "./clients.service";

/**
 * Strażnik routingu (Guard) zarządzający kontrolą dostępu do modułu Rozliczeń (Settlements).
 * Weryfikuje po stronie serwera API, czy zalogowany użytkownik posiada uprawnienia
 * z domeny płatności (Payments) pozwalające na wgląd w bilingi i zestawienia finansowe.
 */
@Injectable({
  providedIn: 'root'
})
export class SettlementsAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private clientsService: ClientsService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) kodu JavaScript
   * modułu rozliczeń. Optymalizuje bezpieczeństwo, zapobiegając transferowi
   * wrażliwej logiki bilingowej do klienta przed potwierdzeniem uprawnień serwerowych.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku posiadania odpowiedniej roli).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/payments-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - payments - ok');
      return true;
    }));
  }

  /**
   * Weryfikuje uprawnienia przed fizyczną aktywacją i wyrenderowaniem interfejsu
   * modułu rozliczeniowego. Gwarantuje integralność sesji poprzez obsługę błędów
   * autoryzacji (HTTP 401) oraz deleguje komunikację o braku wystarczających ról
   * domenowych do współdzielonej instancji CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub bezpieczne drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/payments-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - payments - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
